// =============================================================================
// HopLocalStatus.tsx — "today on this phone" for HOP (10 Oct 2026)
//
// James, 10 Oct 2026, after talking to the crew on the Nullarbor: "a pain point
// even doing offline or with limited coverage they cant see what they have
// uploaded new into which category so maybe a clean banner in the category
// saying if there are photos pending for there and how many or a qty of taken
// today, anything to help them offline so it tracks locally".
//
// Everything here is read from THE PHONE (lib/offline hopLocal): the photos
// still waiting in the queue, and a text ledger of the ones this phone has
// sent. So it is right with no signal at all, and it never waits on Ops.
// It only knows about THIS phone — a mate's photos show once Ops loads.
//
// Exports:
//   useHopLocal(packId)        the live numbers; re-reads when the queue moves
//   <HopCategoryBanner/>       top of a category: taken / uploaded / waiting
//                              today, the upload in progress, anything NOT
//                              SAVED. Replaces the bar that used to sit over
//                              the phone's bottom nav.
//   <HopPendingTiles/>         inside a subcategory: the waiting photos
//                              themselves, tagged "on this phone"
//   <HopPackLocalLine/>        top of the pack: the same three numbers
//   hopCounts(local, key)      per-category / per-subcategory counts for chips
// =============================================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { CloudUpload, CheckCircle2, AlertTriangle, Loader2, Smartphone, RefreshCw, X } from "lucide-react";
import { hopLocal, onQueueChange, getCachedPhoto, syncNow, type HopWaitingRow, type HopSentRow } from "@/lib/offline";

export interface HopLocal { waiting: HopWaitingRow[]; sent: HopSentRow[]; /** mig 441 — full-size copies still to follow, whole pack */ full: number }
const EMPTY: HopLocal = { waiting: [], sent: [], full: 0 };

// Local day key — never toISOString (it shifts the day in AEST).
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const isToday = (iso: string) => dayKey(new Date(iso)) === dayKey(new Date());

export function useHopLocal(packId: string | undefined): HopLocal {
  const [data, setData] = useState<HopLocal>(EMPTY);
  useEffect(() => {
    if (!packId) return;
    let alive = true;
    const read = () => { hopLocal(packId).then(d => { if (alive) setData(d); }).catch(() => {}); };
    read();
    const off = onQueueChange(read);
    const t = setInterval(read, 15_000);
    return () => { alive = false; off(); clearInterval(t); };
  }, [packId]);
  return data;
}

export interface HopCount { waiting: number; lost: number; sentToday: number; takenToday: number }
/** Counts for one category ("cat") or one subcategory ("sub"). */
export function hopCounts(local: HopLocal, key: "cat" | "sub", id: string): HopCount {
  const w = local.waiting.filter(r => r[key] === id);
  const sentToday = local.sent.filter(r => r[key] === id && isToday(r.sent)).length;
  const waiting = w.filter(r => !r.lost).length;
  return { waiting, lost: w.filter(r => r.lost).length, sentToday, takenToday: sentToday + waiting };
}
function packCounts(local: HopLocal): HopCount {
  const sentToday = local.sent.filter(r => isToday(r.sent)).length;
  const waiting = local.waiting.filter(r => !r.lost).length;
  return { waiting, lost: local.waiting.filter(r => r.lost).length, sentToday, takenToday: sentToday + waiting };
}

function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return online;
}

function Stat({ n, label, tone }: { n: number; label: string; tone: "plain" | "ok" | "warn" | "danger" }) {
  const cls = tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : tone === "danger" ? "text-danger" : "text-text";
  return (
    <div className="min-w-0">
      <div className={`text-[22px] leading-none font-semibold tracking-tight ${n === 0 ? "text-text-dim" : cls}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-wider2 text-text-muted mt-1">{label}</div>
    </div>
  );
}

/** Small amber chip: "2 on phone". Renders nothing at zero. */
export function HopWaitingChip({ count, lost = 0 }: { count: number; lost?: number }) {
  if (count === 0 && lost === 0) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono flex-shrink-0 ${
      lost > 0 ? "bg-danger/10 border-danger/30 text-danger" : "bg-warn/10 border-warn/40 text-warn"}`}
      title={lost > 0 ? "A photo's saved copy is gone — it needs retaking" : "Saved on this phone, not in Ops yet"}>
      <Smartphone size={10} /> {count > 0 ? `${count} on phone` : `${lost} lost`}
    </span>
  );
}

export interface BannerJob { id: string; filename: string; phase: string; pct: number; errMsg?: string }

export function HopCategoryBanner({ local, packCategoryId, jobs, onDismissErrors, onSent }: {
  local: HopLocal;
  packCategoryId: string;
  /** The screen's uploads in flight right now. */
  jobs: BannerJob[];
  onDismissErrors: () => void;
  /** A waiting photo for this category went up in the background — reload the grid. */
  onSent: () => void;
}) {
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const c = useMemo(() => hopCounts(local, "cat", packCategoryId), [local, packCategoryId]);

  // background sync sent one → tell the screen so the photo appears in the grid
  const lastSent = useRef(c.sentToday);
  useEffect(() => {
    if (c.sentToday > lastSent.current) onSent();
    lastSent.current = c.sentToday;
  }, [c.sentToday]);   // eslint-disable-line react-hooks/exhaustive-deps

  const active = jobs.filter(j => !["done", "held", "error"].includes(j.phase));
  const errors = jobs.filter(j => j.phase === "error");
  const current = active[0];
  if (c.takenToday === 0 && c.lost === 0 && active.length === 0 && errors.length === 0 && local.full === 0) return null;

  const tone = errors.length > 0 || c.lost > 0 ? "border-danger/40 bg-danger/5"
             : c.waiting > 0 ? "border-warn/40 bg-warn/5"
             : "border-ok/30 bg-ok/5";

  return (
    <div className={`mb-4 rounded-lg border ${tone} p-3.5`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">Today · this phone</div>
        {c.waiting > 0 && active.length === 0 && (
          <button type="button" disabled={busy || !online}
            onClick={async () => { setBusy(true); try { await syncNow(); } finally { setBusy(false); } }}
            className="inline-flex items-center gap-1 text-[11px] text-accent disabled:text-text-dim whitespace-nowrap">
            <RefreshCw size={11} className={busy ? "animate-spin" : ""} /> {busy ? "Trying…" : online ? "Try upload now" : "No signal"}
          </button>
        )}
      </div>

      <div className="mt-2.5 grid grid-cols-3 gap-3">
        <Stat n={c.takenToday} label="taken" tone="plain" />
        <Stat n={c.sentToday} label="uploaded" tone="ok" />
        <Stat n={c.waiting} label="on this phone" tone="warn" />
      </div>

      {current && (
        <div className="mt-3">
          <div className="flex items-center gap-2 text-[12px] text-text">
            <Loader2 size={12} className="text-accent animate-spin flex-shrink-0" />
            <span className="truncate flex-1">
              Uploading{active.length > 1 ? ` · ${active.length} to go` : ""}
              <span className="text-text-muted"> — {current.phase === "watermark" ? "stamping" : current.phase === "save" ? "saving to the pack" : current.phase === "queued" ? "saved, next in line" : "sending"}</span>
            </span>
          </div>
          <div className="mt-1.5 h-1 rounded bg-ink-raised overflow-hidden">
            <div className="h-full bg-accent transition-all" style={{ width: `${Math.max(4, Math.min(100, current.pct || 0))}%` }} />
          </div>
        </div>
      )}

      {!current && c.waiting > 0 && (
        <div className="mt-2.5 text-[12px] text-text-muted flex items-start gap-1.5">
          <CloudUpload size={13} className="text-warn mt-0.5 flex-shrink-0" />
          <span>{c.waiting === 1 ? "1 photo is" : `${c.waiting} photos are`} safe on this phone and will upload by {c.waiting === 1 ? "itself" : "themselves"} while Ops is open with signal. Keep shooting.</span>
        </div>
      )}
      {!current && c.waiting === 0 && c.sentToday > 0 && errors.length === 0 && c.lost === 0 && (
        <div className="mt-2.5 text-[12px] text-ok flex items-center gap-1.5"><CheckCircle2 size={13} /> Everything taken here today is in Ops.</div>
      )}

      {local.full > 0 && (
        <div className="mt-2 text-[11px] text-text-dim">
          {local.full} full-size cop{local.full === 1 ? "y" : "ies"} for this pack still to follow — the photos are already in Ops; the big copies go up when signal allows.
        </div>
      )}

      {c.lost > 0 && (
        <div className="mt-2.5 text-[12px] text-danger flex items-start gap-1.5">
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
          <span>{c.lost} photo{c.lost === 1 ? "" : "s"} here could not be recovered from this phone — take {c.lost === 1 ? "it" : "them"} again.</span>
        </div>
      )}
      {errors.length > 0 && (
        <div className="mt-2.5 rounded-md bg-danger/10 border border-danger/30 p-2.5">
          <div className="flex items-start justify-between gap-2">
            <div className="text-[12px] text-danger font-medium flex items-center gap-1.5"><AlertTriangle size={13} /> {errors.length} photo{errors.length === 1 ? "" : "s"} NOT SAVED — take {errors.length === 1 ? "it" : "them"} again</div>
            <button type="button" onClick={onDismissErrors} className="text-text-muted p-0.5" aria-label="Clear"><X size={14} /></button>
          </div>
          <div className="text-[11px] text-text-muted mt-1 break-words">{(errors[0].errMsg ?? "").replace(/^NOT SAVED — take it again\s*/, "").replace(/^\((.*)\)$/, "$1")}</div>
        </div>
      )}
    </div>
  );
}

/** The waiting photos themselves, for one subcategory (and instance, if any). */
export function HopPendingTiles({ local, packSubcategoryId, instanceId }: {
  local: HopLocal; packSubcategoryId: string; instanceId?: string | null;
}) {
  const rows = useMemo(
    () => local.waiting.filter(r => r.sub === packSubcategoryId && (instanceId == null || r.inst === instanceId)),
    [local, packSubcategoryId, instanceId],
  );
  const ids = rows.map(r => r.local_id).join("|");
  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    let alive = true;
    const made: string[] = [];
    (async () => {
      const next: Record<string, string> = {};
      for (const r of rows) {
        if (r.lost) continue;
        try {
          const c = await getCachedPhoto(r.local_id);
          if (c?.blob && c.blob.type.startsWith("image/")) { const u = URL.createObjectURL(c.blob); made.push(u); next[r.local_id] = u; }
        } catch { /* show the tile without a picture */ }
      }
      if (alive) setUrls(next);
    })();
    return () => { alive = false; made.forEach(u => URL.revokeObjectURL(u)); };
  }, [ids]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (rows.length === 0) return null;
  return (
    <div className="mb-3 rounded-md border border-warn/40 bg-warn/5 p-2">
      <div className="text-[11px] text-warn font-medium mb-1.5 flex items-center gap-1.5">
        <Smartphone size={12} /> {rows.length} on this phone — not in Ops yet
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
        {rows.map(r => (
          <div key={r.local_id} className={`relative aspect-square rounded overflow-hidden border ${r.lost ? "border-danger/50 bg-danger/10" : "border-warn/40 bg-ink-raised"}`}>
            {urls[r.local_id]
              ? <img src={urls[r.local_id]} alt="" className="w-full h-full object-cover opacity-80" />
              : <div className="w-full h-full flex items-center justify-center text-text-dim">{r.lost ? <AlertTriangle size={16} className="text-danger" /> : <CloudUpload size={16} />}</div>}
            <div className={`absolute bottom-0 inset-x-0 text-center text-[8px] font-mono uppercase py-0.5 ${r.lost || r.stuck ? "bg-danger text-white" : "bg-warn text-black"}`}>
              {r.lost ? "lost" : r.stuck ? "stuck" : "waiting"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Top of the pack: the same three numbers, for the whole pack. */
export function HopPackLocalLine({ local }: { local: HopLocal }) {
  const c = useMemo(() => packCounts(local), [local]);
  if (c.takenToday === 0 && c.lost === 0 && local.full === 0) return null;
  return (
    <div className={`mb-4 rounded-lg border px-3.5 py-2.5 flex items-center gap-x-4 gap-y-1 flex-wrap text-[12px] ${
      c.lost > 0 ? "border-danger/40 bg-danger/5" : c.waiting > 0 ? "border-warn/40 bg-warn/5" : "border-ok/30 bg-ok/5"}`}>
      <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">Today · this phone</span>
      <span><span className="font-semibold text-text">{c.takenToday}</span> <span className="text-text-muted">taken</span></span>
      <span><span className="font-semibold text-ok">{c.sentToday}</span> <span className="text-text-muted">uploaded</span></span>
      <span><span className={`font-semibold ${c.waiting > 0 ? "text-warn" : "text-text-dim"}`}>{c.waiting}</span> <span className="text-text-muted">on this phone</span></span>
      {c.lost > 0 && <span className="text-danger">{c.lost} lost — retake</span>}
      {local.full > 0 && <span className="text-text-dim">{local.full} full-size to follow</span>}
    </div>
  );
}
